import { defineCustomElements } from '@ionic/pwa-elements/loader';
import logdna from '@logdna/browser';
import {
  Breadcrumb,
  BrowserTracing,
  reactRouterV5Instrumentation,
  init as sentryInit,
  Replay as SentryReplay
} from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import App from '@components/App';
import SessionStore from '@context/SessionStore';

if (import.meta.env.VITE_MEZMO_INGESTION_KEY) {
  logdna.init(import.meta.env.VITE_MEZMO_INGESTION_KEY);
}

const container = document.getElementById('root');
const root = createRoot(container!);

if (import.meta.env.VITE_SENTRY_DSN) {
  sentryInit({
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'fetch') {
        const url = (breadcrumb?.data?.url as string | null) ?? '';
        if (/graphql/.exec(url)) {
          const input = hint?.input as { body: string }[];
          const body = input[1]?.body;
          if (body) {
            const bodyData = JSON.parse(body) as { [key: string]: unknown };

            const newBreadcrumb: Breadcrumb = {
              category: 'graphql',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              data: bodyData.variables as { [key: string]: any },
              level: 'info',
              message: `${breadcrumb.data?.status_code as number} ${bodyData.operationName as string}`
            };
            return newBreadcrumb;
          }
        }
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      // https://github.com/getsentry/sentry-javascript/issues/2514
      if (hint?.originalException === 'Timeout') {
        return null;
      }
      return event;
    },
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT_NAME,
    ignoreErrors: [
      'Cancel button was pressed',
      "Cannot read property 'isProxied' of undefined",
      "Cannot read properties of undefined (reading 'isProxied')",
      'ChunkLoadError',
      'Failed to fetch',
      'Failed to fetch dynamically imported module',
      'Loading chunk',
      '*GA4React*',
      'InitializationError',
      'invalid_jwt',
      'Load failed',
      'ResizeObserver loop limit exceeded',
      'Unable to preload CSS',
      'unauthorized',
      'AbortError',
      'Cancel button was pressed',
      'Object Not Found Matching Id', // FullStory
      'XMLHttpRequestProgressEvent', // Klaviyo
      "Blocked 'frame-ancestors'", // Not sure why this counts as an error since it's what we want
      'window.interactionRecorder.getMissingFnName'
    ],

    integrations: [
      new BrowserTracing({
        routingInstrumentation: reactRouterV5Instrumentation(history)
      }),
      new SentryReplay({
        maskAllText: true
      })
    ],

    maxValueLength: 20000,
    release: import.meta.env.VITE_RELEASE_SHA ?? undefined,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.0,

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACE_RATE ? parseFloat(import.meta.env.VITE_SENTRY_TRACE_RATE) : 0
  });
}

root.render(
  <HelmetProvider>
    <Helmet>
      <meta content="noindex, nofollow" name="robots" />
      <meta
        content={`
          base-uri 'self';
          child-src 'self' blob:;
          connect-src
            'self'
            data:
            https://static.userguiding.com
            https://ug-client-img.s3.amazonaws.com
            https://api.userguiding.com
            https://nps.userguiding.com
            https://user.userguiding.com
            https://metrics.userguiding.com
            https://ls.userguiding.com
            https://*.sentry.io
            https://*.klaviyo.com
            https://web.delighted.com
            https://www.google-analytics.com
            wss://ws.pusherapp.com
            https://maps.googleapis.com
            https://maps.google.com
            https://us-central1-adaptive-growth.cloudfunctions.net
            https://bat.bing.com
            https://edge.fullstory.com
            https://rs.fullstory.com
            ${import.meta.env.VITE_ACTIONCABLE_URL}
            ${import.meta.env.VITE_API_HOST}
            ${import.meta.env.VITE_CREATE_BLOB_URL}
            http://localhost:3000
            http://google.com
            https://api.ionicjs.com
            https://pro-deploy.ionicjs.com
            https://logs.mezmo.com
            ${import.meta.env.VITE_ATTACHMENT_HOST};
          default-src ;
          font-src
            'self'
            https://fonts.googleapis.com
            https://fonts.gstatic.com
            data:;
          frame-src
            https://www.google.com/
            https://js.stripe.com
            https://player.vimeo.com
            https://maps.google.com
            https://maps.googleapis.com
            https://app.hellosign.com/
            https://community.withgrayce.com
            https://grayce.tribeplatform.com/
            blob:;
          img-src
            'self'
            blob:
            data:
            http://*.klaviyo.com
            https://*.klaviyo.com
            https://www.googletagmanager.com
            https://*.vimeocdn.com
            https://res.cloudinary.com
            https://twemoji.maxcdn.com
            https://static.userguiding.com
            https://ug-client-img.s3.amazonaws.com
            https://rs.fullstory.com
            ${import.meta.env.VITE_ATTACHMENT_HOST}
            ${import.meta.env.VITE_MEDIA_HOST};
          manifest-src
            'self';
          media-src
            'self'
            ${import.meta.env.VITE_ATTACHMENT_HOST}
            ${import.meta.env.VITE_MEDIA_HOST};
          object-src 'none';
          script-src
            'self'
            data:
            'unsafe-inline'
            https://www.google.com
            https://www.gstatic.com/
            https://js.stripe.com
            https://d2yyd1h5u9mauk.cloudfront.net
            https://maps.googleapis.com
            https://maps.gstatic.com
            https://www.googletagmanager.com
            https://maps.googleapis.com
            http://static.klaviyo.com
            https://static.klaviyo.com
            https://static-tracking.klaviyo.com
            https://edge.fullstory.com
            https://rs.fullstory.com
            https://appleid.cdn-apple.com/
            https://static.userguiding.com
            https://*.clevertap-prod.com
            ${import.meta.env.VITE_DEPLOY_HOST};
          style-src
            'self'
            'unsafe-inline'
            https://static.klaviyo.com
            https://static-tracking.klaviyo.com
            https://fonts.googleapis.com;
          worker-src
            'self'
            blob:;
        `}
        httpEquiv="Content-Security-Policy"
      />
      {import.meta.env.VITE_USER_GUIDING_ID && (
        <script>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {`
       (function(g,u,i,d,e,s){g[e]=g[e]||[];var f=u.getElementsByTagName(i)[0];var k=u.createElement(i);k.async=true;k.src='https://static.userguiding.com/media/user-guiding-'+s+'-embedded.js';f.parentNode.insertBefore(k,f);if(g[d])return;var ug=g[d]={q:[]};ug.c=function(n){return function(){ug.q.push([n,arguments])};};var m=['previewGuide','finishPreview','track','triggerNps','hideChecklist','launchChecklist'];for(var j=0;j<m.length;j+=1){ug[m[j]]=ug.c(m[j]);}})(window,document,'script','userGuiding','userGuidingLayer','${
         import.meta.env.VITE_USER_GUIDING_ID
       }');
     `}
        </script>
      )}
    </Helmet>
    <SessionStore>
      <App />
    </SessionStore>
  </HelmetProvider>
);

defineCustomElements(window);
